<script setup lang="ts">
import { select, arc, interpolate, interpolateRgb } from 'd3';
import { FunctionalComponent, onMounted, ref, watchEffect } from 'vue';
import { calculateMetricValue, MetricType, formatDeltaValue, DeltaType } from './../utility';
import { roundAwayFromZero } from '@Shared/utils/util';

import DownArrowSvg from '../visualAssets/DownArrow.svg?component';
import UpArrowSvg from '../visualAssets/UpArrow.svg?component';
import LevelArrowSvg from '../visualAssets/LevelArrow.svg?component';

const svgRef = ref<SVGElement | null>(null);

const props = defineProps<{
  value?: number;
  metricType?: MetricType;
  delta?: number;
  isSkeleton?: boolean;
}>();

const color = computed<string | null>(() => {
  if (props.value == null) return null;

  // handle CSAT colour ranges
  if (props.metricType === MetricType.Csat) {
    if (roundAwayFromZero(props.value * 100) <= 60) {
      return '#D4002B'; // red
    } else if (roundAwayFromZero(props.value * 100) > 60 && roundAwayFromZero(props.value * 100) <= 80) {
      return '#7F7466'; // neutral
    } else {
      return '#0C8A65'; // green
    }
  }

  // Handle raw score average colours
  if (props.metricType === MetricType.RawScoreAverage) {
    if (roundAwayFromZero(props.value) <= 6) {
      return '#D4002B'; // red
    } else if (roundAwayFromZero(props.value) > 6 && roundAwayFromZero(props.value) <= 8) {
      return '#7F7466'; // neutral
    } else {
      return '#0C8A65'; // green
    }
  }

  // else assume that we are working with NPS
  if (roundAwayFromZero(props.value * 100) < 0) {
    return '#D4002B'; // red
  } else if (roundAwayFromZero(props.value * 100) > 0) {
    return '#0C8A65'; // green
  } else {
    return '#7F7466'; // neutral
  }
});

const deltaImg = computed<FunctionalComponent | null>(() => {
  if (props.delta == null) return null;

  if (roundAwayFromZero(props.delta * 100) < 0) {
    return DownArrowSvg;
  } else if (roundAwayFromZero(props.delta * 100) > 0) {
    return UpArrowSvg;
  } else {
    return LevelArrowSvg;
  }
});

const value = computed<string>(() => {
  return calculateMetricValue(props.value, props.metricType ?? MetricType.Nps);
});
const delta = computed<string>(() => {
  if (props.metricType === MetricType.RawScoreAverage) return formatDeltaValue(props.delta, DeltaType.RawDelta);
  return formatDeltaValue(props.delta, DeltaType.PointDelta);
});

let oldAngle = -Math.PI / 2;
let oldColor = '';
let unmount: any = null;

onMounted(() => {
  const svg = select(svgRef.value);

  unmount = watchEffect(() => {
    if (value.value == null || props.value == null || color.value == null) return;

    svg.selectAll('*').remove();

    const gauge = arc()
      .innerRadius(115)
      .outerRadius(130)
      .cornerRadius(30)
      .startAngle(-Math.PI / 2) as any;

    svg
      .append('path')
      .datum({
        endAngle: Math.PI / 2,
      })
      .attr('fill', props.isSkeleton ? '#F9F9F9' : '#D9D9D9')
      .attr('d', gauge);

    const foreground = svg
      .append('path')
      .datum({
        endAngle: oldAngle,
      })
      .attr('fill', props.isSkeleton ? 'url(#line-gradient)' : oldColor)
      .attr('d', gauge);

    if (!props.isSkeleton) {
      svg
        .append('text')
        .text(value.value)
        .attr('text-anchor', 'middle')
        .attr('transform', 'translate(0, -35)')
        .attr('font-size', '50')
        .attr('font-weight', '900')
        .attr('fill', color.value);

      svg.append('line').style('stroke', '#BDBDBD').attr('x1', -50).attr('y1', -27.5).attr('x2', 50).attr('y2', -27.5);

      // adding the min & max labels to the gauge
      let minValue;
      let maxValue;

      // written this way since our default is Nps
      switch (props.metricType) {
        case MetricType.Csat:
          minValue = '0%';
          maxValue = '100%';
          break;
        case MetricType.RawScoreAverage:
          minValue = '0';
          maxValue = '10';
          break;
        default:
          minValue = -100;
          maxValue = '+100';
          break;
      }
      svg
        .append('text')
        .text(minValue)
        .attr('text-anchor', 'middle')
        .attr('transform', 'translate(-123, 15)')
        .attr('font-size', '12')
        .attr('font-weight', '400')
        .attr('fill', '#828282');
      svg
        .append('text')
        .text(maxValue)
        .attr('text-anchor', 'middle')
        .attr('transform', 'translate(120, 15)')
        .attr('font-size', '12')
        .attr('font-weight', '400')
        .attr('fill', '#828282');
    } else {
      svg
        .append('rect')
        .attr('transform', 'translate(-25, -47.5)')
        .attr('rx', 6)
        .attr('ry', 6)
        .attr('width', 50)
        .attr('height', 12.5)
        .attr('fill', 'url(#line-gradient)');

      svg.append('line').style('stroke', '#F9F9F9').attr('x1', -50).attr('y1', -27.5).attr('x2', 50).attr('y2', -27.5);

      svg
        .append('rect')
        .attr('transform', 'translate(-25, -20)')
        .attr('rx', 6)
        .attr('ry', 6)
        .attr('width', 50)
        .attr('height', 12.5)
        .attr('fill', 'url(#line-gradient)');
    }

    let baseValue = props.value;

    const metricType = props.metricType ?? MetricType.Nps;
    // We now support NPS and Csat
    // For NPS we:
    // *100 to get from -1 -> 1 to -100 -> 100
    // Add 100 to get from 0 -> 200 to make percentages easier
    // Division by 200 to get percentage of angle
    // For Csat value is already valid percentage from 0 -> 1
    if (metricType === MetricType.Nps) {
      baseValue = (baseValue * 100 + 100) / 200;
    } else if (metricType === MetricType.RawScoreAverage) {
      // For RawScore average, the raw value is in range 0-10, we have the scale it down
      baseValue = baseValue / 10;
    }
    const angle = (Math.PI / 180) * Math.floor(baseValue * 180 - 90);

    oldAngle = angle;
    const prevColor = oldColor;
    oldColor = color.value;

    if (import.meta.env.VITE_DISABLE_ANIMATION != 'true') {
      const anim = foreground.transition().delay(500).duration(1000);
      if (!props.isSkeleton) anim.styleTween('fill', () => interpolateRgb(prevColor, color.value!));
      anim.call((transition, newAngle) => {
        transition.attrTween('d', (d) => {
          const fill = interpolate(d.endAngle, newAngle);
          return (t) => {
            d.endAngle = fill(t);
            return gauge(d);
          };
        });
      }, angle);
    } else {
      // if we are disabling animations just skip directly to the value
      if (!props.isSkeleton) foreground.style('fill', color.value);
      foreground.attr('d', (d) => {
        d.endAngle = angle;
        return gauge(d);
      });
    }
  });
});

onUnmounted(() => {
  if (unmount) unmount();
});
</script>
<template>
  <svg viewBox="-150 -130 300 150">
    <g ref="svgRef"></g>
    <foreignObject v-if="delta && !isSkeleton" x="-75" y="-20" width="150" height="30">
      <div class="w-max-content mx-auto flex items-center gap-x-1">
        <component width="20" height="20" v-if="deltaImg" :is="deltaImg"></component>

        <p class="inline text-20px font-700 color-[#4F4F4F] m-0">
          {{ delta }}
        </p>
      </div>
    </foreignObject>
  </svg>
</template>
